import { TextElement } from "js/elements/base/TextElement";
import { _ } from "js/vendor";
import * as geom from "js/core/utilities/geom";
import { CollectionElement, CollectionItemElement } from "../base/CollectionElement";
import { TextGroup } from "js/elements/base/TextGroup";
import { ContentElement } from "js/elements/base/ContentElement";
import { SVGCircleElement } from "js/elements/base/SVGElement";
import { AssetType, HorizontalAlignType, TrayType, VerticalAlignType, ContentBlockType } from "common/constants";
import { ELEMENT_TRANSITION_DURATION } from "js/core/utilities/svgHelpers";

class TextGrid extends CollectionElement {
    get defaultItemData() {
        return {
            title: { text: "" },
            body: { text: "" }
        };
    }
}

class TextGridItem extends CollectionItemElement {
    get name() {
        return "Item";
    }
}

class IconTextGrid extends TextGrid {
    getChildItemType() {
        return IconTextGridItem;
    }

    get orientation() {
        return this.model.orientation || this.options.orientation || "auto";
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;
        let layouter = this.getLayouter(this.itemElements, size);

        switch (this.orientation) {
            case "auto":
                this.updateStyles(styles.vertical, true);
                layouter.calcRowLayout({
                    itemOptions: {
                        orientation: "vertical"
                    }
                });

                if (!layouter.isFit || !layouter.isTextFit) {
                    this.updateStyles(styles.horizontal, true);
                    //special case styling when there is a large tray
                    if (this.canvas.model.layout.trayLayout && this.canvas.model.layout.trayLayout.equalsAnyOf(TrayType.RIGHT_INLINE, TrayType.RIGHT_TRAY, TrayType.LEFT_INLINE, TrayType.LEFT_TRAY) && this.canvas.getTrayElement() && this.canvas.getTrayElement().trayWidth > 300) {
                        this.updateStyles(styles.horizontal.withLargeTray, true);
                    }
                    layouter.calcColumnLayout({
                        itemOptions: {
                            orientation: "horizontal"
                        }
                    });
                    layouter.alignHorizontally(HorizontalAlignType.CENTER);
                }
                break;
            case "horizontal":
                this.updateStyles(styles.horizontal, true);
                //special case styling when there is a large tray
                if (this.canvas.model.layout.trayLayout && this.canvas.model.layout.trayLayout.equalsAnyOf(TrayType.RIGHT_INLINE, TrayType.RIGHT_TRAY, TrayType.LEFT_INLINE, TrayType.LEFT_TRAY) && this.canvas.getTrayElement() && this.canvas.getTrayElement().trayWidth > 300) {
                    this.updateStyles(styles.horizontal.withLargeTray, true);
                }
                layouter.calcColumnLayout({ itemOptions: { orientation: "horizontal" } });
                layouter.alignHorizontally(HorizontalAlignType.CENTER);
                break;
            case "vertical":
                this.updateStyles(styles.vertical, true);
                layouter.calcRowLayout({ itemOptions: { orientation: "vertical" } });
                break;
        }

        layouter.alignVertically(VerticalAlignType.MIDDLE);

        props.isFit = layouter.isFit;

        return { size };
        // return { size: layouter.size };
    }
}

class IconTextGridItem extends TextGridItem {
    _build() {
        this.content = this.addElement("content", () => ContentElement, {
            model: this.model,
            defaultAssetType: AssetType.ICON
        });
        this.text = this.addElement("text", () => TextGroup, {
            // allowedBlockTypes: [ContentBlockType.HEADING, ContentBlockType.TITLE, ContentBlockType.BODY, ContentBlockType.BULLET_LIST, ContentBlockType.NUMBERED_LIST],
            autoHeight: true,
            model: this.model,
            title: {
                singleLine: true,
            },
            body: {
                allowParagraphStyles: true,
                allowEmptyLines: false
            }
        });
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        this.updateStyles(styles[options.orientation || "horizontal"], true);

        let layouter = this.getLayouter([this.content, this.text], size);

        if (options.orientation == "vertical") {
            layouter.calcVerticalBlockLayout({
                contentSize: new geom.Size(this.styles.content.width, this.styles.content.height),
                horizontalAlign: layouter.HorizontalAlignType.CENTER
            });
        } else {
            if (options.bulletAlign == "right") {
                this.text.styles.textAlign = HorizontalAlignType.RIGHT;
                this.text.styles.marginRight = this.text.styles.marginLeft;
                this.text.styles.marginLeft = 0;
            }

            layouter.calcHorizontalBlockLayout({
                contentSize: new geom.Size(this.styles.content.width, this.styles.content.height),
                verticalAlign: layouter.VerticalBlockAlignType.MIDDLE_OR_TOP,
                reverse: options.bulletAlign == "right"
            });
        }

        //TODO ?
        // props.children.content.styles = this.styles.content;

        this.isTextFit = layouter.isTextFit;

        return { size: layouter.size };
    }
}

class BulletPoints extends TextGrid {
    get forceSingleColumn() {
        return this.options.forceSingleColumn || this.model.forceSingleColumn;
    }

    get listStyle() {
        return this.options.listStyle || this.model.listStyle;
    }

    getChildItemType() {
        switch (this.model.listStyle) {
            case "numbered":
                return NumberedListItem;
            case "no_bullets":
                return TextListItem;
            case "bullets":
            default:
                return BulletPointItem;
        }
    }

    _loadStyles(styles) {
        //special case styling when there is a large tray
        if (this.canvas.model.layout.trayLayout && this.canvas.model.layout.trayLayout.equalsAnyOf(TrayType.RIGHT_INLINE, TrayType.RIGHT_TRAY, TrayType.LEFT_INLINE, TrayType.LEFT_TRAY) && this.canvas.model.elements.tray && this.canvas.model.elements.tray.trayWidth > 300) {
            styles.applyStyles(styles.withLargeTray);
        }
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        let layouter = this.getLayouter(this.itemElements, size);

        const calcLayout = scale => {
            layouter.calcColumnLayout({
                forceSingleColumn: this.forceSingleColumn,
                listStyle: this.listStyle
            });

            // Layouter is fit, exiting
            if (layouter.isFit) {
                return true;
            }

            // Reducing the scale by 10%
            const reducedScale = scale - 0.1;

            // We can't dowscale anymore
            if (reducedScale < 0.6) {
                return false;
            }

            // Downscaling the styles
            this.scaleStyleValues(reducedScale);

            // Trying again
            return calcLayout(reducedScale);
        };

        props.isFit = calcLayout(1);

        // if we still haven't fit, calc column layout with forceFit
        // if (!layouter.isFit) {
        //     layouter.calcColumnLayout({
        //         forceFit: true,
        //         minItemHeight: 75,
        //         forceSingleColumn: this.forceSingleColumn,
        //         listStyle: this.listStyle,
        //     });
        // }

        layouter.alignHorizontally(styles.hGridAlign);
        layouter.alignVertically(styles.vGridAlign);

        return { size: layouter.size };
    }
}

class TextListItem extends TextGridItem {
    get minHeight() {
        return this.text.minHeight;
    }

    get passThroughSelection() {
        return false;
    }

    _build() {
        this.text = this.addElement("text", () => TextGroup, {
            allowedBlockTypes: [ContentBlockType.HEADING, ContentBlockType.TITLE, ContentBlockType.BODY, ContentBlockType.BULLET_LIST, ContentBlockType.NUMBERED_LIST],
            autoHeight: true,
            model: this.model,
            showAddButton: true,
            title: {
                singleLine: true
            },
            body: {
                allowParagraphStyles: true,
                allowEmptyLines: false,
                placeholder: "Type text or hit enter to add another bullet"
            }
        });
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        // if (options.scale) {
        //     styles.scaleTextStyles(options.scale);
        // }

        let textProps = children.setProps(this.text, size);
        props.isFit = true;

        return { size: textProps.size };
    }
}

class BulletPointItem extends TextListItem {
    getElementTreeType() {
        return "TextListItem";
    }

    _build() {
        this.bullet = this.addElement("bullet", () => SVGCircleElement);
        this.text = this.addElement("text", () => TextGroup, {
            // allowedBlockTypes: [ContentBlockType.HEADING, ContentBlockType.TITLE, ContentBlockType.BODY, ContentBlockType.BULLET_LIST, ContentBlockType.NUMBERED_LIST],
            // autoHeight: true,
            model: this.model,
            showAddButton: false,
            title: {
                singleLine: true
            },
            body: {
                allowParagraphStyles: true,
                allowEmptyLines: false,
                placeholder: "Type text or hit enter to add another bullet"
            }
        });
    }

    _calcProps(props, options) {
        let { size, children, styles } = props;

        // if (options.scale) {
        //     styles.scaleTextStyles(options.scale);
        // }

        if (options.bulletAlign == "right") {
            this.text.styles.textAlign = HorizontalAlignType.RIGHT;
            this.text.styles.marginRight = this.text.styles.marginLeft;
            this.text.styles.marginLeft = 0;
        }

        let layouter = this.getLayouter([this.bullet, this.text], size);
        layouter.calcHorizontalBlockLayout({
            contentSize: new geom.Size(this.styles.bullet.width, this.styles.bullet.height),
            verticalAlign: layouter.VerticalBlockAlignType.MIDDLE_TITLE,
            reverse: options.bulletAlign == "right"
        });

        props.isFit = this.text.isTextFit;

        return { size: layouter.size };
    }
}

class NumberedListItem extends TextListItem {
    getElementTreeType() {
        return "TextListItem";
    }

    _build() {
        this.index = this.addElement("index", () => TextElement, {
            model: {
                index: this.itemIndex + 1
            },
            canEdit: false,
            isTabbable: false
        });
        this.text = this.addElement("text", () => TextGroup, {
            autoHeight: true,
            model: this.model,
            showAddButton: false,
            title: {
                singleLine: true
            },
            body: {
                allowParagraphStyles: true,
                allowEmptyLines: false,
                placeholder: "Type or hit enter to add another bullet"
            }
        });
    }

    _calcProps(props, options) {
        let { size, styles, children } = props;

        // if (options.scale) {
        //     this.scaleStyleValues(options.scale);
        // }

        if (options.bulletAlign == "right") {
            this.text.styles.textAlign = HorizontalAlignType.RIGHT;
            this.text.styles.marginRight = this.text.styles.marginLeft;
            this.text.styles.marginLeft = 0;
        }

        let layouter = this.getLayouter([this.index, this.text], size);
        layouter.calcHorizontalBlockLayout({
            verticalAlign: layouter.VerticalBlockAlignType.MIDDLE_TITLE,
            reverse: options.bulletAlign == "right"
        });

        return { size: layouter.size };
    }
}

class ImageCarousel extends TextGrid {
    get previousIndex() {
        return this._previousIndex;
    }

    get currentIndex() {
        return this._currentIndex;
    }

    set currentIndex(index) {
        this._previousIndex = this._currentIndex;
        if (index < 0) {
            this._currentIndex = this.itemCollection.length - index % -this.itemCollection.length;
        } else {
            this._currentIndex = index % this.itemCollection.length;
        }
    }

    setupElement() {
        this._currentIndex = 0;
        this._previousIndex = 0;
    }

    getChildItemType() {
        return ImageCarouselItem;
    }

    _calcProps(props, options) {
        const { size, children } = props;

        this.itemElements.forEach(element => children.setProps(element, size));

        this.buildPlaybackStages();

        return { size };
    }

    addItem(props, index) {
        super.addItem(props, index);
        this.buildPlaybackStages();
    }

    deleteItem(itemId) {
        super.deleteItem(itemId);
        this.currentIndex = Math.max(this.currentIndex - 1, 0);
        this.buildPlaybackStages();
    }

    buildPlaybackStages() {
        this.canvas.clearPlaybackStages();
        for (let index = 0; index < this.itemElements.length; index++) {
            this.canvas.addPlaybackStage({
                type: "carousel",
                index,
                callback: (stage, animate) => this.goToItem(stage.index, animate)
            });
        }
    }

    goNextItem() {
        return this.goToItem(this.currentIndex + 1, true);
    }

    goPrevItem() {
        return this.goToItem(this.currentIndex - 1, true);
    }

    async goToItem(index, animate) {
        this.currentIndex = index;
        // No need to recalc here
        this.canvas.refreshRender(animate, false);
        // Resolve only after the animation finishes
        if (animate) {
            await new Promise(resolve => setTimeout(resolve, ELEMENT_TRANSITION_DURATION));
        }
    }

    renderChildren(props, transition) {
        return this.itemElements.map((element, index) => {
            let animationName = null;
            let renderTransition = false;
            if (index !== this.currentIndex) {
                // Non current elements will still be rendered (but with 0 opacity)
                // in order to get all assets preloaded and cached
                element.calculatedProps.opacity = 0;
            } else {
                if (transition && this.currentIndex !== this.previousIndex) {
                    // Current element changed -> animate slide in/out
                    animationName = this.previousIndex < this.currentIndex ? "element-slide-in-right" : "element-slide-in-left";
                    renderTransition = true;
                }
                element.calculatedProps.opacity = 1;
            }
            return element.renderElement(element.calculatedProps, renderTransition, { animationName });
        });
    }

    get disableAllAnimationsByDefault() {
        return true;
    }

    get animationElementName() {
        return "Carousel";
    }

    get animateChildren() {
        return false;
    }

    _getAnimations() {
        return [{
            name: "Fade in",
            prepare: () => {
                this.animationState.fadeInProgress = 0;
            },
            onBeforeAnimationFrame: progress => {
                this.animationState.fadeInProgress = progress;
            }
        }];
    }
}

class ImageCarouselItem extends TextGridItem {
    get previousIndex() {
        return this.parentElement.previousIndex;
    }

    get currentIndex() {
        return this.parentElement.currentIndex;
    }

    get canSelectChildElements() {
        return this.itemIndex === this.parentElement.currentIndex;
    }

    get canSelect() {
        return this.itemIndex === this.parentElement.currentIndex;
    }

    get canRollover() {
        return this.itemIndex === this.parentElement.currentIndex;
    }

    _build() {
        this.content = this.addElement("content", () => ContentElement, {
            model: this.model,
            defaultAssetType: AssetType.ICON
        });
        this.text = this.addElement("text", () => TextGroup, {
            model: this.model,
            body: {
                allowParagraphStyles: true,
            },
            title: {
                singleLine: true
            }
        });
    }

    _loadStyles(styles) {
        if (this.parentElement.model.shapeStyle) {
            styles.ContentElement.decoration.shape = this.parentElement.model.shapeStyle;
        }
    }

    _calcProps(props, options) {
        const { size, children } = props;

        const contentSize = new geom.Size(size.width / 2, size.height).square();

        const contentProps = children.setProps(this.content, contentSize);
        const textProps = children.setProps(this.text, new geom.Size(size.width - contentSize.width, size.height));

        contentProps.bounds = new geom.Rect(0, size.height / 2 - contentProps.size.height / 2, contentProps.size);
        textProps.bounds = new geom.Rect(contentSize.width, size.height / 2 - textProps.size.height / 2, textProps.size);

        return { size };
    }

    renderElement(...args) {
        // Force disable default overlay for non current items before rendering
        this.content.options.forceHideDefaultOverlay = !this.canSelect;
        return super.renderElement(...args);
    }
}

export {
    TextGrid,
    BulletPoints,
    IconTextGrid,
    ImageCarousel,
    BulletPointItem,
    NumberedListItem,
    TextListItem,
    IconTextGridItem,
};

export const elements = {
    IconTextGrid,
    BulletPoints,
    ImageCarousel,
};
